import React, { useEffect, useState, useCallback, useRef } from 'react';
import './App.css';
import Header from './components/common/Header';
import useFormState from './hooks/useFormState';
import useFormNavigation from './hooks/useFormNavigation';


// Import all form pages
import Page1PersonalInfo from './components/FormPages/page1_PersonalInfo';
import Page2NPSPage from './components/FormPages/page2_NPS';
import Page3MultipleChoice from './components/FormPages/page3_MultipleChoice';
import Page4AgreementAndFrontline from './components/FormPages/page4_AgreementAndFrontline';
import Page5Rating from './components/FormPages/page5_Rating';
import Page6Feedback from './components/FormPages/page6_Feedback';
import Page7ProductFeedback from './components/FormPages/page7_ProductFeedback';
import Page8RatingBasedCommunication from './components/FormPages/page8_RatingBasedCommunication';
import Page9AwarenessER from './components/FormPages/page9_AwarenessER';
import Page10AwarenessERFeedbackAndReasons from './components/FormPages/page10_AwarenessERFeedbackAndReasons';
import Page11MonthlyInvoice from './components/FormPages/page11_MonthlyInvoice';
import Page12ThankYou from './components/FormPages/page12_ThankYou';
import Page13Socials from './components/FormPages/page13_Socials';



function App() {
  // Add a formId state at the top with other state declarations
  const [formId, setFormId] = useState('');
  
  // Initialize the formId with the current URL when the component mounts
  useEffect(() => {
    // Get the full URL including any query parameters
    const currentUrl = window.location.href;
    setFormId(currentUrl);
    console.log('Using URL as form ID:', currentUrl);
  }, []);

  // Form navigation state and handlers
  const {
    currentPage,
    setCurrentPage,
  } = useFormNavigation();

  // Form state and handlers
  const {
    // State organized by page order
    // Page 1: Personal Info
    personalInfo,
    
    // Page 2: NPS
    npsScore,
    npsFeedback,
    
    // Page 3: Benefits
    selectedBenefits,
    otherBenefit,
    
    // Page 4: Agreement and Frontline
    agreementResponses,
    frontlineCareResponse,
    
    // Page 5: Treatment Ratings
    treatmentRatings,
    
    // Page 6: Service Feedback
    serviceFeedback,
    
    // Page 7: Product Feedback
    productFeedback,
    radarUsage,
    
    // Page 8: Communication Ratings
    communicationRatings,
    
    // Page 9: ER Awareness
    erAwareness,
    
    // Page 10: ER Rating and Reasons
    erRating,
    ratingReason,
    
    // Page 11: Monthly Invoice (combined with feedback)
    invoiceClarity,
    invoiceFeedback,
    
    // Form submission state
    isSubmitting,
    error,

    // Handler functions (keep these in the same order as they were)
    updatePersonalInfo,
    updateTreatmentRating,
    updateServiceFeedback,
    updateCommunicationRating,
    toggleBenefit,
    updateAgreementResponse,
    resetForm,

    // Setters (keep these in the same order as the state variables above)
    setNpsScore,
    setNpsFeedback,
    setOtherBenefit,
    setFrontlineCareResponse,
    setErAwareness,
    setErRating,
    setRatingReason,
    setInvoiceClarity,
    setInvoiceFeedback,
    setIsSubmitting,
    updateProductFeedback,
    setRadarUsage,
  } = useFormState();

  // Add mobile detection
  const [isMobile, setIsMobile] = React.useState(false);
  
  // Detect mobile devices on component mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simplify handleNext to remove partial submissions
  const [navigationPath, setNavigationPath] = useState([1]);

  const [lastActivity, setLastActivity] = useState(Date.now());
  const [submittedPages, setSubmittedPages] = useState({});
  const INACTIVITY_THRESHOLD = 10800000; // 3 hours of inactivity
  const submissionTimeoutRef = useRef(null);

  // Add this state variable near your other state declarations
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleNext = () => {
    console.log("Current page:", currentPage);
    console.log("Current erAwareness value:", erAwareness);

    // Submit partial data before navigating
    submitPartialData();

    let nextPage = currentPage + 1;

    // Special case logic for ER Services awareness
    if (currentPage === 9) {
      console.log("Page 9 navigation logic - erAwareness:", erAwareness);
      
      if (erAwareness === 'yes') {
        console.log("Condition met: erAwareness is 'yes', going to page 10");
        nextPage = 10; // Go to the merged feedback and reasons page
      } else if (erAwareness === 'no') {
        console.log("Condition met: erAwareness is 'no', skipping to page 11");
        nextPage = 11; // Skip to the monthly invoice page
      }
    }
    // Skip page 11 after page 10 since we merged it
    else if (currentPage === 10) {
      console.log("Page 10 navigation logic - skipping page 11");
      nextPage = 11; // Skip to the monthly invoice page
    }
    // Remove the conditional logic for page 11
    // Now page 11 always goes to page 12 (Thank You)
    else if (currentPage === 11) {
      console.log("Page 11 navigation logic - going to thank you page");
      nextPage = 12;
    }

    console.log("Navigation path:", navigationPath);
    console.log("Moving from page", currentPage, "to page", nextPage);

    setCurrentPage(nextPage);
    setNavigationPath(prevPath => [...prevPath, nextPage]);
  };

  const handlePrevious = () => {
    console.log("Current page:", currentPage);
    
    // Get the previous page from navigation path
    const prevPath = [...navigationPath];
    prevPath.pop(); // Remove current page
    const prevPage = prevPath[prevPath.length - 1];
    
    console.log("Navigation path:", navigationPath);
    console.log("Moving from page", currentPage, "to page", prevPage);
    
    setCurrentPage(prevPage);
    setNavigationPath(prevPath);
  };

  // Update the renderCurrentPage function to handle conditional rendering
  const renderCurrentPage = () => {
    // For the thank you page, don't show progress
    if (currentPage === 14) {
      return (
        <div className="thank-you-container">
          <Page13Socials />
        </div>
      );
    }
    
    // For all other pages, show the question box with progress
    return (
      <>
        <div className="question-box">
          {renderQuestionPage()}
        </div>
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentPage / 12) * 100}%` }}
            ></div>
          </div>
          <div className="progress-text">
            <span className="progress-step">Step {currentPage}</span>
            <span>of 12</span>
          </div>
        </div>
      </>
    );
  };

  const renderQuestionPage = () => {
    switch (currentPage) {
      case 1:
        return (
          <Page1PersonalInfo 
            personalInfo={personalInfo}
            updatePersonalInfo={updatePersonalInfo}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
          />
        );
      case 2:
        return (
          <Page2NPSPage 
            npsScore={npsScore}
            npsFeedback={npsFeedback}
            setNpsScore={setNpsScore}
            setNpsFeedback={setNpsFeedback}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 3:
        return (
          <Page3MultipleChoice
            selectedBenefits={selectedBenefits}
            otherBenefit={otherBenefit}
            toggleBenefit={toggleBenefit}
            setOtherBenefit={setOtherBenefit}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 4:
        return (
          <Page4AgreementAndFrontline 
            agreementResponses={agreementResponses}
            updateAgreementResponse={updateAgreementResponse}
            frontlineCareResponse={frontlineCareResponse}
            setFrontlineCareResponse={setFrontlineCareResponse}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 5:
        return (
          <Page5Rating 
            treatmentRatings={treatmentRatings}
            updateTreatmentRating={updateTreatmentRating}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
            isMobile={isMobile}
          />
        );
      case 6:
        return (
          <Page6Feedback 
            serviceFeedback={serviceFeedback}
            updateServiceFeedback={updateServiceFeedback}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 7:
        return (
          <Page7ProductFeedback
            productFeedback={productFeedback}
            updateProductFeedback={updateProductFeedback}
            radarUsage={radarUsage}
            setRadarUsage={setRadarUsage}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 8:
        return (
          <Page8RatingBasedCommunication
            communicationRatings={communicationRatings}
            updateCommunicationRating={updateCommunicationRating}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 9:
        return (
          <Page9AwarenessER 
            erAwareness={erAwareness}
            setErAwareness={setErAwareness}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 10:
        return (
          <Page10AwarenessERFeedbackAndReasons
            erRating={erRating}
            setErRating={setErRating}
            ratingReason={ratingReason}
            setRatingReason={setRatingReason}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 11:
        return (
          <Page11MonthlyInvoice
            invoiceClarity={invoiceClarity}
            setInvoiceClarity={setInvoiceClarity}
            invoiceFeedback={invoiceFeedback}
            setInvoiceFeedback={setInvoiceFeedback}
            handlePrevious={handlePrevious}
            handleNext={handleNext}
          />
        );
      case 12:
        return (
          <Page12ThankYou
            handlePrevious={handlePrevious}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return <div>Page not found</div>;
    }
  };

  // Helper function to find the selected option
  const getSelectedOption = (questionResponses) => {
    const options = ['Strongly Agree', 'Agree', 'Disagree', 'Strongly Disagree'];
    for (let i = 1; i <= 4; i++) {
      if (questionResponses[`option${i}`]) {
        return options[i-1];
      }
    }
    return '';
  };

  // Modify the handleSubmit function to set isFormSubmitted to true on successful submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    setIsSubmitting(true);
    console.log("Starting form submission...");
    
    try {
      // Prepare the complete submission data
      const completeSubmission = {
        'formId': formId,
        'capturedAt': new Date().toLocaleString(),
        'submissionType': 'complete',
        
        // Page 1: Personal Info
        'personalInfo.name': personalInfo.name,
        'personalInfo.email': personalInfo.email,
        'personalInfo.hospital': personalInfo.hospital,
        'personalInfo.role': personalInfo.role,
        
        // Page 2: NPS
        'nps.score': npsScore,
        'nps.feedback': npsFeedback,
        
        // Page 3: Benefits
        'benefits.selected': selectedBenefits.join(', '),
        'benefits.other': otherBenefit,
        
        // Page 4: Agreement and Frontline
        'agreement.question1': getSelectedOption(agreementResponses.question1),
        'agreement.question2': getSelectedOption(agreementResponses.question2),
        'frontlineCare.response': frontlineCareResponse,
        
        // Page 5: Treatment Ratings
        'treatment.planRating': treatmentRatings.treatmentPlan,
        'treatment.healthImprovement': treatmentRatings.healthImprovement,
        'treatment.hourlyMonitoring': treatmentRatings.hourlyMonitoring,
        
        // Page 6: Service Feedback
        'feedback.physicianConsult': serviceFeedback.physicianConsult,
        'feedback.nursingServices': serviceFeedback.nursingServices,
        'feedback.concerns': serviceFeedback.concerns,
        
        // Page 7: Product Feedback
        'productFeedback.digitization': getSelectedOption(productFeedback.digitization),
        'productFeedback.radarUsage': radarUsage,
        'productFeedback.radarFeatures': productFeedback.radarFeatures,
        
        // Page 8: Communication Ratings
        'communication.icu': communicationRatings.icu,
        'communication.cst': communicationRatings.cst,
        
        // Page 9: ER Services Awareness
        'erServices.awareness': erAwareness,
        
        // Page 10: ER Services Rating and Reasons
        'erServices.rating': erRating,
        'erServices.reason': ratingReason,
        
        // Page 11: Monthly Invoice (combined with feedback)
        'invoice.clarity': invoiceClarity,
        'invoice.feedback': invoiceFeedback
      };

      console.log('Submitting form data:', completeSubmission);
      
      const webhookURL = "https://hook.eu1.make.com/gf705za2in46sapmy7517fu8vqnxj2m3";
      
      const response = await fetch(webhookURL, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json"
        },
        body: JSON.stringify(completeSubmission),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      console.log("Form submitted successfully");
      
      // Set form as submitted to disable inactivity tracking
      setIsFormSubmitted(true);
      
      // Clear any pending inactivity timeout
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
        submissionTimeoutRef.current = null;
      }
      
      // Navigate to the socials page after successful submission
      setCurrentPage(14); // Update to page 14 for socials
      setIsSubmitting(false);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert("There was a problem submitting your feedback. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Function to submit partial data
  const submitPartialData = useCallback(() => {
    // Check if we've already submitted data for this page recently
    if (submittedPages[currentPage] && 
        Date.now() - submittedPages[currentPage] < 10000) { // 10 seconds threshold
      console.log(`Already submitted data for page ${currentPage} recently`);
      return;
    }

    const partialSubmission = {
      'formId': formId,
      'capturedAt': new Date().toLocaleString(),
      'submissionType': 'partial',
      'currentPage': currentPage,
      
      // Page 1: Personal Info
      'personalInfo.name': personalInfo.name,
      'personalInfo.email': personalInfo.email,
      'personalInfo.hospital': personalInfo.hospital,
      'personalInfo.role': personalInfo.role,
      
      // Page 2: NPS
      'nps.score': npsScore,
      'nps.feedback': npsFeedback,
      
      // Page 3: Benefits
      'benefits.selected': selectedBenefits.join(', '),
      'benefits.other': otherBenefit,
      
      // Page 4: Agreement and Frontline
      'agreement.question1': getSelectedOption(agreementResponses.question1),
      'agreement.question2': getSelectedOption(agreementResponses.question2),
      'frontlineCare.response': frontlineCareResponse,
      
      // Page 5: Treatment Ratings
      'treatment.planRating': treatmentRatings.treatmentPlan,
      'treatment.healthImprovement': treatmentRatings.healthImprovement,
      'treatment.hourlyMonitoring': treatmentRatings.hourlyMonitoring,
      
      // Page 6: Service Feedback
      'feedback.physicianConsult': serviceFeedback.physicianConsult,
      'feedback.nursingServices': serviceFeedback.nursingServices,
      'feedback.concerns': serviceFeedback.concerns,
      
      // Page 7: Product Feedback
      'productFeedback.digitization': getSelectedOption(productFeedback.digitization),
      'productFeedback.radarUsage': radarUsage,
      'productFeedback.radarFeatures': productFeedback.radarFeatures,
      
      // Page 8: Communication Ratings
      'communication.icu': communicationRatings.icu,
      'communication.cst': communicationRatings.cst,
      
      // Page 9: ER Services Awareness
      'erServices.awareness': erAwareness,
      
      // Page 10: ER Services Rating and Reasons
      'erServices.rating': erRating,
      'erServices.reason': ratingReason,
      
      // Page 11: Monthly Invoice (combined with feedback)
      'invoice.clarity': invoiceClarity,
      'invoice.feedback': invoiceFeedback,
    };

    console.log('Submitting partial data for page:', currentPage);
    
    const webhookURL = "https://hook.eu1.make.com/gf705za2in46sapmy7517fu8vqnxj2m3";
    
    fetch(webhookURL, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json"
      },
      body: JSON.stringify(partialSubmission),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      console.log('Partial submission saved successfully');
      // Record that we've submitted this page and when
      setSubmittedPages(prev => ({
        ...prev,
        [currentPage]: Date.now()
      }));
    })
    .catch(error => {
      console.error('Error saving partial submission:', error);
      // Fallback to localStorage if the server request fails
      try {
        localStorage.setItem(`formPartial_${formId}_page_${currentPage}`, JSON.stringify(partialSubmission));
      } catch (storageError) {
        console.error('Failed to save to localStorage:', storageError);
      }
    });
  }, [formId, currentPage, personalInfo, npsScore, npsFeedback, selectedBenefits, otherBenefit, 
      agreementResponses, frontlineCareResponse, treatmentRatings, serviceFeedback, 
      communicationRatings, erAwareness, erRating, ratingReason, invoiceClarity, 
      invoiceFeedback, productFeedback, radarUsage, submittedPages]);

  // Modify the inactivity tracking effect to check currentPage
  useEffect(() => {
    // Skip setting up inactivity tracking if:
    // 1. Form is already submitted, OR
    // 2. User is still on the first page
    if (isFormSubmitted || currentPage === 1) {
      console.log(isFormSubmitted 
        ? 'Form already submitted, inactivity tracking disabled' 
        : 'User on first page, inactivity tracking not started yet');
      return;
    }

    console.log('Starting inactivity tracking from page', currentPage);

    const trackActivity = () => {
      setLastActivity(Date.now());
      
      // Clear any pending submission timeout
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }
      
      // Set a new timeout for inactivity
      submissionTimeoutRef.current = setTimeout(() => {
        // Don't submit if form is already submitted
        if (!isFormSubmitted) {
          console.log('User inactive, submitting partial data');
          submitPartialData();
        }
      }, INACTIVITY_THRESHOLD);
    };

    // Add event listeners for user activity
    window.addEventListener('mousemove', trackActivity);
    window.addEventListener('keypress', trackActivity);
    window.addEventListener('click', trackActivity);
    window.addEventListener('scroll', trackActivity);
    
    // Initial activity tracking
    trackActivity();

    return () => {
      // Clean up event listeners
      window.removeEventListener('mousemove', trackActivity);
      window.removeEventListener('keypress', trackActivity);
      window.removeEventListener('click', trackActivity);
      window.removeEventListener('scroll', trackActivity);
      
      // Clear any pending timeout
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }
    };
  }, [submitPartialData, isFormSubmitted, currentPage]); // Add currentPage to dependency array

  // Also modify the beforeunload handler to check currentPage
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Only submit partial data if:
      // 1. Form hasn't been fully submitted yet, AND
      // 2. User has progressed beyond the first page
      if (!isFormSubmitted && currentPage > 1) {
        submitPartialData();
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [submitPartialData, isFormSubmitted, currentPage]); // Add currentPage to dependency array

  return (
    <div className={`app ${isMobile ? 'mobile-view' : ''}`}>
      <Header isMobile={isMobile} />
      <main className="main-content">
        <div className="form-container">
          {renderCurrentPage()}
        </div>
      </main>
    </div>
  );
}

export default App;